import React from 'react';
import { motion } from 'framer-motion';
import { Award, Camera, Users, Clock, Compass, Star, Gift } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import img from "./logo.png"
// Bu bileşen, önceki tasarımdan daha modern ve etkileşimli bir görünüm sunar.
// Ana Sayfanızın tasarım diline daha yakın, yumuşak gradientler ve derinlik kullanır.

const logoUrl = img;

// Rozet kartları için özel, glassmorphism efektli bileşen
const BadgeCard = ({ icon, title, description, reward, delay }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <motion.div
      ref={ref}
      className="relative p-6 md:p-8 rounded-3xl overflow-hidden backdrop-blur-md border border-gray-700/50 transform transition-all duration-300"
      // Glassmorphism için background gradient'i
      style={{ background: 'linear-gradient(135deg, rgba(31, 41, 55, 0.5) 0%, rgba(17, 24, 39, 0.4) 100%)' }}
      // Kartın sayfaya girişi için animasyon
      initial={{ opacity: 0, scale: 0.8, y: 50 }}
      animate={inView ? { opacity: 1, scale: 1, y: 0 } : {}}
      transition={{ duration: 0.1, delay: delay, ease: "easeOut" }}
      // Hover animasyonu: parlama efekti ve büyüme
      whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(52, 211, 153, 0.6)" }}
    >
      <div className="flex flex-col items-center text-center">
        <div className="mb-4 p-4 bg-indigo-600 rounded-full inline-flex items-center justify-center shadow-lg">
          {icon}
        </div>
        <h3 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-cyan-300 mb-2">{title}</h3>
        <p className="text-gray-300 text-lg mb-4">{description}</p>
        <div className="flex items-center gap-2 mt-auto p-2 bg-teal-500/20 text-teal-300 rounded-full text-base font-semibold">
          <Gift size={18} />
          <span className="text-sm md:text-base">{reward}</span>
        </div>
      </div>
    </motion.div>
  );
};

// Ana rozet sistemi sayfası bileşeni
export default function BadgeSystemPage() {
  const badges = [
    {
      icon: <Camera size={36} className="text-white" />,
      title: "İlk Keşif",
      description: "İlk fotoğrafını yükle ve bir tarihi mekanı başarıyla keşfet.",
      reward: "Özel profil simgesi"
    },
    {
      icon: <Compass size={36} className="text-white" />,
      title: "Gezgin",
      description: "Farklı şehirlerde 10 tarihi mekanı keşfet ve bu rozeti kazan.",
      reward: "Sistemdeki tüm mekanlara erken erişim"
    },
    {
      icon: <Users size={36} className="text-white" />,
      title: "Sosyal Elçi",
      description: "Gezdiğin 10 mekana yorum bırak ve rozet kazan.",
      reward: "Toplulukta 'Elçi' etiketi"
    },
    {
      icon: <Clock size={36} className="text-white" />,
      title: "Gece Kuşu",
      description: "Gece saatlerinde 3 tarihi mekanı başarıyla keşfetmenin ödülü.",
      reward: "Gece modu teması"
    },
    {
      icon: <Award size={36} className="text-white" />,
      title: "Tarih Uzmanı",
      description: "10’dan fazla tarihi mekanı doğru tanımlayarak bilgi seviyeni kanıtla.",
      reward: "Forumda “Uzman” etiketi ile öne çık."
    },
    {
      icon: <Star size={36} className="text-white" />,
      title: "Yükselen Yıldız",
      description: "Toplamda 100 mekan taraması ve 30 yorum atarak topluluğun en popüler üyelerinden biri ol.",
      reward: "Özel arayüz renkleri"
    },
  ];

  return (
    <div className="font-sans text-white min-h-screen antialiased overflow-x-hidden relative">
      {/* Arka plan için yumuşak radyal gradient ve bulutsu efekti */}
      <div className="absolute inset-0 bg-gray-950 -z-10">
        <div className="w-96 h-96 bg-purple-500/20 rounded-full blur-[150px] absolute top-10 left-10 animate-pulse"></div>
        <div className="w-80 h-80 bg-teal-500/20 rounded-full blur-[150px] absolute bottom-20 right-20 animate-pulse delay-500"></div>
      </div>

      <div className="p-6 md:p-12 relative z-10">
        {/* Sayfa başlığı ve tanıtım bölümü */}
        <motion.div
          className="max-w-4xl mx-auto text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src={logoUrl} alt="Mekânname Logo" className="w-16 h-16 rounded-full border-2 border-indigo-500 shadow-lg" />
            <h1 className="text-4xl md:text-6xl font-extrabold bg-gradient-to-r from-teal-400 to-indigo-400 bg-clip-text text-transparent">
              Mekânname Rozet Sistemi
            </h1>
          </div>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
            Keşiflerinizle ilgili başarılarınızı sergileyin ve toplulukta kendinizi gösterin. Her rozet, Mekânname'deki maceranızın bir sembolüdür.
          </p>
        </motion.div>

        {/* Rozetleri görüntüleme bölümü */}
        <section className="max-w-7xl mx-auto mb-16">
          <h2 className="text-center text-3xl md:text-4xl font-extrabold mb-12 text-indigo-400">
            Rozet Koleksiyonu ve Ödülleri
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {badges.map((badge, index) => (
              <BadgeCard
                key={index}
                icon={badge.icon}
                title={badge.title}
                description={badge.description}
                reward={badge.reward}
                delay={index * 0.1} // Her kartın farklı zamanda girmesi için delay eklendi
              />
            ))}
          </div>
        </section>

        {/* Rozet kazanma ipuçları bölümü */}
        <section className="max-w-6xl mx-auto text-center">
          <h2 className="text-center text-3xl md:text-4xl font-extrabold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-teal-400">
            Nasıl Rozet Kazanılır?
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-8">
            Rozet kazanmak için yapman gerekenler çok basit! Uygulamayı kullanarak keşifler yap, paylaşımlarda bulun ve toplulukla etkileşim kur. Ne kadar aktif olursan, o kadar çok rozet kazanırsın.
          </p>
          <motion.div
            className="flex flex-col md:flex-row justify-center items-stretch gap-6 md:gap-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex-1 p-6 bg-gray-800/70 rounded-2xl shadow-xl border border-gray-700/50 flex flex-col items-center gap-3">
              <Camera size={48} className="text-teal-400" />
              <h4 className="text-xl font-bold text-gray-100">1. Keşfet ve Fotoğraf Yükle</h4>
              <p className="text-gray-400 text-center">Tarihi mekanları keşfet ve ilk fotoğrafını yükleyerek macerana başla.</p>
            </div>
            <div className="flex-1 p-6 bg-gray-800/70 rounded-2xl shadow-xl border border-gray-700/50 flex flex-col items-center gap-3">
              <Users size={48} className="text-indigo-400" />
              <h4 className="text-xl font-bold text-gray-100">2. Toplulukla Etkileşime Geç</h4>
              <p className="text-gray-400 text-center">Mekanlara yorum yaparak kullanıcılar ile etkileşimde ol.</p>
            </div>
            <div className="flex-1 p-6 bg-gray-800/70 rounded-2xl shadow-xl border border-gray-700/50 flex flex-col items-center gap-3">
              <Award size={48} className="text-yellow-400" />
              <h4 className="text-xl font-bold text-gray-100">3. Rozetlerini Kazan</h4>
              <p className="text-gray-400 text-center">Aktifliğin, keşiflerin ve katkılarınla özel rozetleri toplamaya başla!</p>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
}
